/** 轻量 Canvas 折线图（F-018 指标时序）。无第三方依赖；数据量大时可换 uPlot。 */
import { useEffect, useRef } from 'react';
import { THEME } from '../../render/theme';

export interface Line {
  label: string;
  color: string;
  /** [x, y] 点序列（x 为仿真毫秒，y 为指标值）。 */
  data: [number, number][];
}

interface Props {
  lines: Line[];
  height?: number;
  yLabel?: string;
}

function fmtX(ms: number): string {
  return `${(ms / 60000).toFixed(0)}m`;
}

export function LineChart({ lines, height = 170, yLabel }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(ctx, w, height, lines, yLabel);
  }, [lines, height, yLabel]);

  return <canvas ref={ref} style={{ width: '100%', height }} className="rounded-md border border-slate-800 bg-[#0f1115]" />;
}

function draw(ctx: CanvasRenderingContext2D, w: number, h: number, lines: Line[], yLabel?: string) {
  ctx.clearRect(0, 0, w, h);
  const padL = 44;
  const padR = 8;
  const padT = 10;
  const padB = 24;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  const allPts = lines.flatMap((l) => l.data);
  const xMax = Math.max(1, ...allPts.map((p) => p[0]));
  const yMax = Math.max(1, ...allPts.map((p) => p[1]));

  // 轴
  ctx.strokeStyle = THEME.grid;
  ctx.fillStyle = THEME.textDim;
  ctx.lineWidth = 1;
  ctx.font = '10px ui-monospace, monospace';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i <= 4; i++) {
    const y = padT + (plotH * i) / 4;
    const val = yMax * (1 - i / 4);
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(w - padR, y);
    ctx.stroke();
    ctx.fillText(val.toFixed(val >= 100 ? 0 : 1), padL - 4, y);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let i = 0; i <= 4; i++) {
    const x = padL + (plotW * i) / 4;
    ctx.fillText(fmtX((xMax * i) / 4), x, h - padB + 4);
  }

  // 折线
  for (const line of lines) {
    if (line.data.length === 0) continue;
    ctx.strokeStyle = line.color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < line.data.length; i++) {
      const [x, y] = line.data[i]!;
      const px = padL + (x / xMax) * plotW;
      const py = padT + (1 - y / yMax) * plotH;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }

  // 图例
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let lx = padL;
  for (const line of lines) {
    ctx.fillStyle = line.color;
    ctx.fillRect(lx, padT - 2, 8, 2);
    ctx.fillStyle = THEME.textDim;
    ctx.fillText(line.label, lx + 12, padT);
    lx += ctx.measureText(line.label).width + 40;
  }

  if (yLabel) {
    ctx.save();
    ctx.translate(11, h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = THEME.textDim;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();
  }
}
